import { Component, OnInit, Input, ViewChild, ElementRef, Renderer2, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NotificationService } from '../../services/notification.service';
import { NotificationTypeEnum } from 'src/app/core/enums/notificationTypeEnum';
import { NotificationModel } from '../../models/notificationModel';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-notifications-list',
    templateUrl: './notifications-list.component.html',
    styleUrls: ['./notifications-list.component.scss'],
    standalone: false
})
export class NotificationsListComponent implements OnInit, OnDestroy {
  unsubscribe = new Subject<void>();
  notifications: NotificationModel[];

  @ViewChild('notificationsContainer') notificationsContainer: ElementRef;
  _rightOffset: number;
  get rightOffset(): number {
    return this._rightOffset;
  }
  @Input() set rightOffset(value: number) {
    this._rightOffset = value;
    if (this.notificationsContainer) {
      this.renderer.setStyle(this.notificationsContainer.nativeElement, 'right', `${value}px`);
    }
  }

  constructor(
    private readonly notificationService: NotificationService,
    private readonly renderer: Renderer2,
    private readonly translateService: TranslateService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.translateService.onLangChange
    .pipe(
      takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.notifications.forEach(n => this.translateNotification(n));
    });

    this.notifications = [];
    this.notificationService.getNotifications().pipe(takeUntil(this.unsubscribe)).subscribe(notification => {
      this.translateNotification(notification);
      this.notifications.push(notification);
      this.triggerChangeDetection();
    });

    this.notificationService.shouldCloseNotifications()
        .pipe(filter(r => r === true && this.notifications.length > 0),
        takeUntil(this.unsubscribe)).subscribe(() => {
          this.closeAllNotifications();
    });
  }

  closeNotification(notification) {
    this.notifications = this.notifications.filter(n => n.id !== notification.id);
    this.triggerChangeDetection();
  }

  closeAllNotifications() {
    this.notifications = [];
    this.triggerChangeDetection();
  }

  triggerChangeDetection() {
    this.changeDetector.detectChanges();
  }

  getNotificationStyle(notificationType: NotificationTypeEnum) {
    switch (notificationType) {
      case NotificationTypeEnum.Success:
        return 'success-notification';
      case NotificationTypeEnum.Error:
        return 'error-notification';
      case NotificationTypeEnum.Warning:
        return 'warning-notification';
      default:
        return '';
    }
  }

  translateNotification(notification: NotificationModel) {
    if (notification.requiresTranslation) {
      if (notification.translationParams) {
        const translationParams = {};
        for (const param in notification.translationParams) {
          if (notification.translationParams.hasOwnProperty(param)) {
            translationParams[param] = notification.translationParams[param];
          }
        }
        notification.message = this.translateService.instant(notification.translationKey, translationParams);
      } else {
        notification.message = this.translateService.instant(notification.translationKey);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
